import global from "../../../utilities/global";
import localStorage from "../../../utilities/localStorage";
import Cache from "./cache";
import { ACCESS_TOKEN, GLOBAL_AUTH_CACHE_NAME, ID_TOKEN, REFRESH_TOKEN, SUBJECT_OF_TOKEN } from "./constants";
import { AuthenticationError } from "./errors";
import { verify as verifyIdToken } from "./jwt";
import TransactionManager from "./transaction-manager";
import {
    bufferToBase64UrlEncoded,
    createQueryParams,
    createRandomString,
    encodeState,
    getUniqueScopes,
    isCognito,
    oauthToken,
    parseQueryResult,
    sha256,
} from "./utils";
import { LOGOUT_REASON } from "./constants";

/**
 * OAuth SDK for Single Page Applications using "Authorization Code Grant Flow with PKCE"
 * https://backstage.forgerock.com/docs/am/6.5/oidc1-guide/#openid-connect-authorization-code-flow-pkce
 * based on https://github.com/auth0/auth0-spa-js/tree/master/src
 */

const DEFAULT_SCOPE = "openid";
const DEFAULT_AUDIENCE = "default";
const AUTH_PROVIDER_OPENAM = "openam";

export default class AuthClient {
    constructor(options) {
        this.options = options;
        this.scope = getUniqueScopes(DEFAULT_SCOPE, options.scope);
        this.audience = this.options.audience || DEFAULT_AUDIENCE;
        this.cache = new Cache();
        this.transactionManager = new TransactionManager();
        this.authEndpoint = this.options.authEndpoint;
        this.tokenEndpoint = this.options.tokenEndpoint;
        this.logoutEndpoint = this.options.logoutEndpoint;
        this.tokenIssuer = this.options.issuer;
        this.requiredRole = this.options.requiredRole;
        this.authProvider = this.options.authProvider;
    }

    _url(endpoint, path) {
        return endpoint + (endpoint.indexOf("?") === -1 ? "?" : "&") + path;
    }

    _getParams(
        authorizeOptions,
        state,
        nonce,
        code_challenge,
        redirect_uri
    ) {
        const {...withoutDomain} = this.options;
        return {
            ...withoutDomain,
            ...authorizeOptions,
            scope: getUniqueScopes(
                this.scope,
                authorizeOptions.scope
            ),
            response_type: "code",
            response_mode: "query",
            state,
            nonce,
            redirect_uri: redirect_uri || this.options.redirect_uri,
            code_challenge,
            code_challenge_method: "S256"
        };
    }

    _authorizeUrl(authorizeOptions) {
        return this._url(this.authEndpoint, createQueryParams(authorizeOptions));
    }

    _verifyIdToken(id_token, nonce) {
        return verifyIdToken({
            iss: this.tokenIssuer,
            aud: this.options.client_id,
            id_token,
            nonce,
            leeway: this.options.leeway,
            max_age: this._parseNumber(this.options.max_age)
        });
    }

    _parseNumber(value) {
        if (typeof value !== "string") {
            return value;
        }
        return parseInt(value, 10) || undefined;
    }

    /**
     * Builds an `/authorize` URL for loginWithRedirect using the parameters
     * provided as arguments. Random and secure `state` and `nonce`
     * parameters will be auto-generated.
     *
     * @param options
     */

    async buildAuthorizeUrl(
        options = {}
    ) {
        const {redirect_uri, appState, ...authorizeOptions} = options;
        const stateIn = encodeState(createRandomString());
        const nonceIn = createRandomString();
        const code_verifier = createRandomString();
        const code_challengeBuffer = await sha256(code_verifier);
        const code_challenge = bufferToBase64UrlEncoded(code_challengeBuffer);
        const fragment = options.fragment ? `#${options.fragment}` : "";
        const params = this._getParams(
            authorizeOptions,
            stateIn,
            nonceIn,
            code_challenge,
            redirect_uri
        );
        const url = this._authorizeUrl(params);
        this.transactionManager.create(stateIn, {
            nonce: nonceIn,
            code_verifier,
            appState,
            scope: params.scope,
            audience: params.audience || DEFAULT_AUDIENCE
        });
        return url + fragment;
    }

    /**
     * Returns the user information if available (decoded
     * from the `id_token`).
     */
    getUser() {
        const cache = this.getCacheEntry();
        return cache && cache.decodedToken.user;
    }

    getDecodedToken() {
        const cache = this.getCacheEntry();
        return cache && cache.decodedToken;
    }

    /**
     * Performs a redirect to `/authorize` using the parameters
     * provided as arguments. Random and secure `state` and `nonce`
     * parameters will be auto-generated.
     *
     * @param options
     */
    async loginWithRedirect(options = {}) {
        const url = await this.buildAuthorizeUrl(options);
        window.location.assign(url);
    }

    /**
     * After the browser redirects back to the callback page,
     * call `handleRedirectCallback` to handle success and error
     * responses from OAuth provider. If the response is successful, results
     * will be valid according to their expiration times.
     */
    async handleRedirectCallback(
        url = window.location.href
    ) {
        const queryStringFragments = url.split("?").slice(1);
        if (queryStringFragments.length === 0) {
            throw new Error("There are no query params available for parsing.");
        }
        const {state, code, error, error_description} = parseQueryResult(
            queryStringFragments.join("")
        );

        if (error) {
            this.transactionManager.remove(state);
            throw new AuthenticationError(error, error_description, state);
        }

        const transaction = this.transactionManager.get(state);
        if (!transaction) {
            throw new Error("Invalid state (transaction)");
        }
        this.transactionManager.remove(state);

        const authResult = await oauthToken({
            tokenEndpoint: this.tokenEndpoint,
            "grant_type": "authorization_code",
            audience: this.options.audience,
            client_id: this.options.client_id,
            code_verifier: transaction.code_verifier,
            redirect_uri: this.options.redirect_uri,
            code,
        });

        localStorage.setItem(ID_TOKEN, authResult.id_token);
        localStorage.setItem(ACCESS_TOKEN, authResult.access_token);
        localStorage.setItem(REFRESH_TOKEN, authResult.refresh_token);

        const verifyToken = this._verifyIdToken(
            authResult.id_token,
            transaction.nonce
        );
        verifyToken.user.id = verifyToken.user[SUBJECT_OF_TOKEN];
        verifyToken.user.email = verifyToken.user["cognito:username"];
        const decodedToken = verifyToken;
        if (!this.userHasRequiredScopeRole(decodedToken)) {
            throw new Error("Insufficient roles");
        }
        const cacheEntry = {
            ...authResult,
            decodedToken,
            audience: transaction.audience,
            scope: transaction.scope
        };
        this.cache.save(cacheEntry);

        global.set(GLOBAL_AUTH_CACHE_NAME, {
            ...authResult,
            decodedToken,
            audience: this.audience,
            scope: this.scope
        });

        return {
            appState: transaction.appState
        };
    }

    async handleRefreshOnInit() {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN);

        const authResult = await oauthToken({
            tokenEndpoint: this.tokenEndpoint,
            grant_type: REFRESH_TOKEN,
            client_id: this.options.client_id,
            refresh_token: refreshToken,
        });

        localStorage.setItem(ID_TOKEN, authResult.id_token);
        localStorage.setItem(ACCESS_TOKEN, authResult.access_token);
        if(authResult.refresh_token)
        {
            localStorage.setItem(REFRESH_TOKEN, authResult.refresh_token);
        }

        const verifyToken = this._verifyIdToken(authResult.id_token);
        verifyToken.user.id = verifyToken.user[SUBJECT_OF_TOKEN];
        verifyToken.user.email = verifyToken.user["cognito:username"];
        const decodedToken = verifyToken;
        if (!this.userHasRequiredScopeRole(decodedToken)) {
            throw new Error("Insufficient roles");
        }
        const cacheEntry = {
            ...authResult,
            decodedToken,
            audience: this.audience,
            scope: this.scope,
        };
        this.cache.save(cacheEntry);

        global.set(GLOBAL_AUTH_CACHE_NAME, {
            ...authResult,
            decodedToken,
            audience: this.audience,
            scope: this.scope
        });
    }

    userHasRequiredScopeRole(decodedToken) {
        if (isCognito(this.options.issuer)) {
            const rolesUserHas = decodedToken.user["cognito:groups"];
            return this.requiredRole === null || rolesUserHas?.includes(this.requiredRole);
        }

        if (this.isOpenAm()) {
            const scopes = this.scope.split(" ");
            const profile = scopes[scopes.length - 1];
            const rolesUserHas = decodedToken.user[profile];
            return rolesUserHas.length > 0;
        }
        return false;
    }

    /**
     * Returns `true` if there's valid information stored, otherwise returns `false`.
     */
    isAuthenticated() {
        const user = this.getUser();
        return !!user;
    }

    /**
     * Do a logout and redirect if there is a options.returnTo
     * options contain a reason for the logout, which is used to redirect to the corresponding page.
     *
     * @param options
     */
    logout(options) {
        localStorage.setItem(LOGOUT_REASON, options);
        const idToken = localStorage.getItem(ID_TOKEN);
        const logoutUrl = this.isOpenAm()
            ? this._url(this.logoutEndpoint, createQueryParams({
                "id_token_hint": idToken,
            }))
            : this._url(this.logoutEndpoint, createQueryParams({
                "client_id": this.options.client_id,
                "id_token_hint": idToken,
                "logout_uri": this.options.logout_uri,
            }));
        
        localStorage.removeItem(ID_TOKEN);
        localStorage.removeItem(ACCESS_TOKEN);
        localStorage.removeItem(REFRESH_TOKEN);

        window.location.assign(logoutUrl);
        
        if (!isCognito(this.options.issuer)) {
            window.location.replace("/logout");
        }
    }

    isOpenAm() {
        return this.options.authProvider === AUTH_PROVIDER_OPENAM;
    }

    getCacheEntry() {
        return this.cache.get({
            scope: this.scope,
            audience: this.audience,
        });
    }

}
